import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLanguage } from './LanguageContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { loadLanguageFromProfile, changeLanguage } = useLanguage();

  useEffect(() => {
    // Check if user is authenticated on app load
    checkAuth();
    
    // Handle OAuth callback with user data in URL fragment
    const handleAuthCallback = async () => {
      if (window.location.hash.includes('auth-success=')) {
        try {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const userDataParam = hashParams.get('auth-success');
          if (userDataParam) {
            const userData = JSON.parse(decodeURIComponent(userDataParam));
            localStorage.setItem('musiker-user', JSON.stringify(userData));
            // Load full profile data including permissions (mark as login)
            await loadUserProfile(userData, true);
            // Clear the hash from URL
            window.history.replaceState(null, null, window.location.pathname);
          }
        } catch (error) {
          console.error('Failed to process auth callback:', error);
        }
      }
    };
    
    handleAuthCallback();
  }, []);

  const loadUserProfile = async (baseUser, isLogin = false) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const loginParam = isLogin ? '&isLogin=true' : '';
      const response = await fetch(`${API_BASE}/api/user?operation=getAccount&userId=${baseUser.id}${loginParam}`);

      if (response.ok) {
        const profileData = await response.json();
        // Merge Google OAuth data with profile data
        const fullUser = {
          ...baseUser,
          ...profileData,
          // Keep Google OAuth fields that might be different
          id: baseUser.id,
          name: baseUser.name,
          image: baseUser.image
        };
        setUser(fullUser);

        // Set flag to indicate user has signed in before
        localStorage.setItem('musiker-has-signed-in', 'true');

        // Load and set user's language preference
        if (profileData.languagePreference) {
          changeLanguage(profileData.languagePreference);
        }

        return fullUser;
      } else if (response.status === 404) {
        // User doesn't exist in database, create them
        console.log('AuthContext: User not found, creating new user account');

        const createResponse = await fetch(`${API_BASE}/api/user?operation=createAccount&userId=${baseUser.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: baseUser.email,
            displayName: baseUser.name,
            googleImageUrl: baseUser.image,
            languagePreference: 'en'
          }),
        });

        if (createResponse.ok) {
          const newUserData = await createResponse.json();
          console.log('AuthContext: Successfully created new user');

          // Merge Google OAuth data with created user data
          const fullUser = {
            ...baseUser,
            ...newUserData,
            id: baseUser.id,
            name: baseUser.name,
            image: baseUser.image
          };
          setUser(fullUser);
          localStorage.setItem('musiker-has-signed-in', 'true');
          return fullUser;
        } else {
          console.error('AuthContext: Failed to create user account');
          // Fallback to basic user data
          setUser(baseUser);
          localStorage.setItem('musiker-has-signed-in', 'true');
          return baseUser;
        }
      } else {
        console.error('AuthContext: Unexpected error loading profile:', response.status);
        // Fallback to basic user data
        setUser(baseUser);
        localStorage.setItem('musiker-has-signed-in', 'true');
        return baseUser;
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Fallback to basic user data
      setUser(baseUser);
      localStorage.setItem('musiker-has-signed-in', 'true');
      return baseUser;
    }
  };

  const checkAuth = async () => {
    try {
      // Check if user data is stored locally (since we're using manual OAuth)
      const userData = localStorage.getItem('musiker-user');
      if (userData) {
        const baseUser = JSON.parse(userData);
        // Load full profile data including permissions
        await loadUserProfile(baseUser);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = () => {
    // Use current domain for API calls on Vercel, fallback to configured URL for local dev
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? '' // Use relative URL on Vercel (same domain)
      : (process.env.REACT_APP_API_BASE_URL || '');
    window.location.href = `${apiUrl}/api/auth/signin`;
  };

  const signInWithEmail = async (email, password) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store user data and update state
        localStorage.setItem('musiker-user', JSON.stringify(data.user));
        localStorage.setItem('musiker-has-signed-in', 'true');
        setUser(data.user);

        // Set user's language preference
        if (data.user.languagePreference) {
          changeLanguage(data.user.languagePreference);
        }

        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error, requiresVerification: data.requiresVerification };
      }
    } catch (error) {
      console.error('Email sign in failed:', error);
      return { success: false, error: 'Sign in failed. Please try again.' };
    }
  };

  const registerWithEmail = async (email, password, displayName) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, displayName }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message, user: data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Email registration failed:', error);
      return { success: false, error: 'Registration failed. Please try again.' };
    }
  };

  const signOut = async () => {
    try {
      // Clear local storage and user state
      localStorage.removeItem('musiker-user');
      setUser(null);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signInWithEmail,
    registerWithEmail,
    signOut,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};